import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Gift, Copy, Share2, Users, Mic2, Loader2, ArrowDownToLine, X, Check, Clock, CalendarDays, Banknote } from "lucide-react";
import { toast } from "sonner";

/** Referral programmes — "Artiste Ambassadeur" (artists) and "Fan Influenceur" (fans).
 * Rules and amounts are all computed by the server (backend/affiliates.py); this page
 * only shows totals — never who bought what. */

// thousands separated by a plain space (toLocaleString is inconsistent across browsers and Android)
export const nf = (n) => String(Math.round(n || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
export const fcfa = (usd, rate) => `${nf((usd || 0) * (rate || 600))} FCFA`;
const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const PAYOUT_STATUS = { PENDING: "En cours", PROCESSING: "En cours", COMPLETED: "Payé", FAILED: "Échoué (montant rendu)", CANCELLED: "Annulé" };

export function programText(key) {
  return key === "ambassador"
    ? {
        title: "Artiste Ambassadeur",
        pitch: "Partage ton code promo. Chaque personne qui crée son compte avec ton code devient ton affilié : pendant 12 mois, tu gagnes une commission sur tous ses achats de musique, et sur toutes ses ventes si c'est un artiste.",
        perk: "Un artiste qui s'inscrit avec ton code promo reçoit 3 mois gratuits en plus (6 mois au lieu de 3).",
        condition: "Condition : avoir publié au moins une chanson sur Metazik.",
      }
    : {
        title: "Fan Influenceur",
        pitch: "Partage ton code promo. Chaque personne qui crée son compte avec ton code devient ton affilié : pendant 12 mois, tu gagnes une commission sur tous ses achats de musique, et sur toutes ses ventes si c'est un artiste.",
        perk: "Un artiste qui s'inscrit avec ton code promo reçoit 3 mois gratuits en plus (6 mois au lieu de 3).",
        condition: "Condition : avoir acheté au moins 5 chansons sur Metazik (un projet acheté en entier compte pour 1).",
      };
}

export default function AffiliatePage() {
  const { user, refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  const load = () => api.get("/affiliate/me").then((r) => setMe(r.data)).catch(() => setMe({ eligible: false }));
  useEffect(() => { load(); }, []);

  if (!me) return <div className="grid place-items-center py-16"><Loader2 className="w-8 h-8 text-zinc-500 animate-spin" /></div>;
  if (!me.eligible) {
    return <div className="max-w-2xl mx-auto px-4 text-zinc-400 py-12 text-center">Le programme d'affiliation est réservé aux comptes fans et artistes.</div>;
  }
  const text = programText(me.program.key);
  const rate = me.xaf_rate;
  const link = me.code ? `${window.location.origin}/auth?ref=${me.code}` : "";

  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/affiliate/join");
      setMe(data);
      refresh();
      toast.success(data.member ? "Bienvenue dans le programme ! Voici ton code." : "Demande enregistrée : ton code promo sera créé automatiquement dès que tu rempliras la condition.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Réessaie dans un instant.");
    } finally {
      setBusy(false);
    }
  };
  const copy = async (value, what) => {
    try { await navigator.clipboard.writeText(value); toast.success(`${what} copié`); } catch { toast.error("Copie impossible, sélectionne-le à la main."); }
  };
  const share = async () => {
    const msg = `Rejoins-moi sur Metazik avec mon code promo ${me.code} : ${link}`;
    if (navigator.share) { try { await navigator.share({ title: "Metazik", text: msg, url: link }); } catch {} } else copy(msg, "Message");
  };

  const b = me.balance;
  const progress = Math.min(100, (b.available / me.min_withdrawal_usd) * 100);
  const maxMonth = Math.max(0.000001, ...me.monthly.map((m) => m.amount_usd));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-5" data-testid="affiliate-page">
      <header className="anim-in">
        <p className="text-xs uppercase tracking-[0.25em] text-[#FF0050] font-bold flex items-center gap-2"><Gift className="w-4 h-4" /> Affiliation</p>
        <h1 className="text-3xl sm:text-4xl font-black font-display text-white">{text.title}</h1>
        <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{text.pitch}</p>
      </header>

      {!me.member ? (
        <div className="rounded-3xl p-5 border border-white/10 bg-white/[0.03] space-y-3" data-testid="affiliate-join-card">
          <ul className="text-sm text-zinc-300 space-y-1.5 list-disc pl-5">
            <li>Commission de {String(me.rate_purchase_pct).replace(".", ",")} % de la part nette de Metazik sur chaque achat ou vente d'un de tes affiliés, pendant 12 mois.</li>
            <li>{text.perk}</li>
            <li>Retrait possible dès que ton solde atteint {nf(me.min_withdrawal_xaf)} FCFA.</li>
            <li>Participer est gratuit et sans obligation.</li>
          </ul>
          {me.requirement && (
            <div className={`rounded-2xl px-4 py-3 text-sm border ${me.requirement.met ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-200" : "bg-white/5 border-white/10 text-zinc-300"}`} data-testid="affiliate-requirement">
              <p>{me.requirement.text}</p>
              <p className="text-xs mt-1 opacity-80">Où tu en es : {me.requirement.have} / {me.requirement.needed}</p>
            </div>
          )}
          {me.waiting ? (
            <p className="text-sm text-amber-200" data-testid="affiliate-waiting">Ta demande est enregistrée : ton code promo sera créé automatiquement dès que tu rempliras la condition. Tu recevras une notification.</p>
          ) : me.status === null && (
            <button onClick={join} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="affiliate-join-btn">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Je participe
            </button>
          )}
        </div>
      ) : (
        <>
          {me.status !== "active" && (
            <p className="text-sm rounded-2xl px-4 py-3 bg-amber-500/10 border border-amber-500/20 text-amber-200" data-testid="affiliate-status-note">
              {me.status === "disabled" && "Ta participation est désactivée : tu ne gagnes plus de commissions, mais tu peux retirer ton solde."}
              {me.status === "suspended" && "Ta participation est suspendue le temps d'une vérification : commissions et retraits sont en pause."}
              {me.status === "blocked" && "Ta participation au programme a été bloquée. Contacte le support pour en savoir plus."}
            </p>
          )}
          <div className="rounded-3xl p-5 border border-white/10 bg-gradient-to-br from-[#0A0A0A] via-zinc-950 to-[#1a0a14]">
            <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">Ton code promo</p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-3xl font-black font-display tracking-[0.15em] text-white" data-testid="affiliate-code">{me.code}</span>
              <button onClick={() => copy(me.code, "Code")} className="btn-secondary text-xs inline-flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copier</button>
              <button onClick={share} className="btn-primary text-xs inline-flex items-center gap-1.5" data-testid="affiliate-share"><Share2 className="w-3.5 h-3.5" /> Partager</button>
            </div>
            <button onClick={() => copy(link, "Lien")} className="mt-2 text-xs text-zinc-400 hover:text-white underline break-all text-left">{link}</button>
            <p className="mt-3 text-xs text-zinc-500">Le code promo se saisit uniquement à la création du compte, et un compte ne peut utiliser qu'un seul code.</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-2xl bg-white/5 p-3"><Users className="w-4 h-4 text-[#00F2FE]" /><p className="text-xl font-black text-white mt-1">{me.referred.fans}</p><p className="text-[11px] text-zinc-500">fans affiliés (inscrits avec ton code)</p></div>
              <div className="rounded-2xl bg-white/5 p-3"><Mic2 className="w-4 h-4 text-[#FF0050]" /><p className="text-xl font-black text-white mt-1">{me.referred.artists}</p><p className="text-[11px] text-zinc-500">artistes affiliés (inscrits avec ton code)</p></div>
            </div>
          </div>

          <section className="rounded-3xl p-5 border border-white/10 bg-white/[0.03]" data-testid="affiliate-balance">
            <h2 className="text-[11px] uppercase tracking-[0.25em] text-[#00F2FE] font-black flex items-center gap-2"><Banknote className="w-4 h-4" /> Solde des commissions</h2>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Total gagné" value={fcfa(b.lifetime_earned, rate)} />
              <Stat label="Disponible" value={fcfa(b.available, rate)} strong testid="affiliate-available" />
              <Stat label="Retrait en cours" value={fcfa(b.pending_payout, rate)} />
              <Stat label="Déjà retiré" value={fcfa(b.lifetime_withdrawn, rate)} />
            </div>
            <div className="mt-4">
              <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#00F2FE] to-[#8A2BE2]" style={{ width: `${progress}%` }} /></div>
              <p className="text-[11px] text-zinc-500 mt-1.5">
                {me.can_withdraw ? "Tu peux demander un retrait." : `Retrait possible à partir de ${nf(me.min_withdrawal_xaf)} FCFA. En dessous, tes commissions restent dans ton solde et s'additionnent.`}
              </p>
              <button onClick={() => setWithdrawing(true)} disabled={!me.can_withdraw} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#00F2FE] to-[#8A2BE2] text-black font-black text-xs disabled:opacity-40 disabled:cursor-not-allowed" data-testid="affiliate-withdraw-open">
                <ArrowDownToLine className="w-3.5 h-3.5" /> Demander un retrait
              </button>
            </div>

            <h3 className="mt-6 text-xs font-bold text-zinc-300">Évolution par mois</h3>
            {me.monthly.length === 0 ? (
              <p className="text-xs text-zinc-500 mt-2">Pas encore de commission. Partage ton code pour commencer.</p>
            ) : (
              <div className="mt-3 flex items-end gap-2 h-32" data-testid="affiliate-monthly">
                {me.monthly.map((m) => (
                  <div key={m.month} className="flex-1 min-w-0 flex flex-col items-center justify-end h-full gap-1">
                    <span className="text-[9px] text-zinc-400 tabular-nums">{nf(m.amount_usd * rate)}</span>
                    <div className="w-full max-w-[28px] rounded-t-md bg-gradient-to-t from-[#8A2BE2] to-[#FF0050]" style={{ height: `${Math.max(4, (m.amount_usd / maxMonth) * 100)}%` }} />
                    <span className="text-[9px] text-zinc-500">{MONTHS[parseInt(m.month.slice(5), 10) - 1]}</span>
                  </div>
                ))}
              </div>
            )}

            {me.payouts.length > 0 && (
              <>
                <h3 className="mt-6 text-xs font-bold text-zinc-300">Mes retraits</h3>
                <div className="mt-2 space-y-1.5">
                  {me.payouts.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2 text-xs">
                      {p.status === "COMPLETED" ? <Check className="w-4 h-4 text-emerald-400" /> : p.status === "FAILED" ? <X className="w-4 h-4 text-red-400" /> : <Clock className="w-4 h-4 text-amber-400" />}
                      <span className="text-white font-bold tabular-nums">{nf(p.payout_amount || 0)} {p.payout_currency}</span>
                      <span className="text-zinc-500">{new Date(p.requested_at).toLocaleDateString("fr-FR")}</span>
                      <span className="ml-auto text-zinc-400">{PAYOUT_STATUS[p.status] || p.status}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </>
      )}

      {me.free_until && (
        <p className="text-xs text-zinc-400 flex items-center gap-2" data-testid="affiliate-free-until">
          <CalendarDays className="w-4 h-4 text-[#00F2FE]" /> Ta période gratuite d'artiste court jusqu'au {new Date(me.free_until).toLocaleDateString("fr-FR")}
          {me.was_referred ? " (3 mois offerts en plus grâce au code promo utilisé à ton inscription)." : "."}
        </p>
      )}

      {withdrawing && <CommissionWithdraw me={me} onClose={() => setWithdrawing(false)} onDone={() => { setWithdrawing(false); load(); }} />}
    </div>
  );
}

function Stat({ label, value, strong, testid }) {
  return (
    <div className="rounded-2xl bg-white/5 p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{label}</p>
      <p className={`mt-1 font-black tabular-nums ${strong ? "text-lg text-white" : "text-sm text-zinc-200"}`}>{value}</p>
    </div>
  );
}

/** Same withdrawal path as artists' sales money (POST /payouts) — here for commissions. */
function CommissionWithdraw({ me, onClose, onDone }) {
  const amount = Math.floor(me.balance.available * 100 + 1e-6) / 100;
  const [providers, setProviders] = useState(null);
  const [key, setKey] = useState("");
  const [method, setMethod] = useState("");
  const [msisdn, setMsisdn] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [busy, setBusy] = useState(false);
  const offer = providers?.find((p) => `${p.id}::${p.payout_currency}` === key) || null;

  useEffect(() => {
    api.get("/payouts/providers").then(({ data }) => {
      const list = data?.providers || [];
      setProviders(list);
      if (list.length) {
        setKey(`${list[0].id}::${list[0].payout_currency}`);
        if (list[0].operators?.length) setMethod(list[0].operators[0].code);
      }
    }).catch(() => setProviders([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!offer || !method || !msisdn || !firstName || !lastName) return;
    setBusy(true);
    try {
      await api.post("/affiliate/withdraw", {
        amount, payout_currency: offer.payout_currency, provider: offer.id,
        destination: { method, msisdn: msisdn.trim(), first_name: firstName.trim(), last_name: lastName.trim() },
      });
      toast.success("Demande de retrait envoyée ✓");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-xl grid place-items-center p-4" data-testid="affiliate-withdraw-modal">
      <form onSubmit={submit} className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-3xl p-5 relative space-y-3">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-full bg-white/5 text-zinc-400" aria-label="close"><X className="w-4 h-4" /></button>
        <h3 className="text-xl font-black font-display text-white">Retrait des commissions</h3>
        <p className="text-sm text-zinc-300">Montant : <strong>{fcfa(amount, me.xaf_rate)}</strong> (tout ton solde disponible)</p>
        <p className="text-xs text-amber-200/90" data-testid="affiliate-withdraw-fees">Les frais Mobile Money du retrait sont à ta charge : ils sont déduits du montant que tu reçois.</p>
        {providers === null && <p className="text-xs text-zinc-500 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…</p>}
        {providers?.length === 0 && <p className="text-xs text-amber-300">Aucun moyen de retrait disponible en ce moment.</p>}
        {offer && (
          <>
            <div>
              <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">Opérateur Mobile Money</span>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {(offer.operators || []).map((op) => (
                  <button key={op.code} type="button" onClick={() => setMethod(op.code)}
                    className={`px-3 py-2.5 rounded-xl border text-sm font-semibold ${method === op.code ? "border-[#00F2FE] bg-[#00F2FE]/10 text-white" : "border-white/10 bg-white/5 text-zinc-300"}`}>
                    {op.label}
                  </button>
                ))}
              </div>
              {!(offer.operators || []).length && <input value={method} onChange={(e) => setMethod(e.target.value)} placeholder="Opérateur" className="input-base mt-1" />}
            </div>
            <input value={msisdn} onChange={(e) => setMsisdn(e.target.value)} placeholder="Numéro Mobile Money" className="input-base" data-testid="affiliate-withdraw-phone" />
            <div className="grid grid-cols-2 gap-2">
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Prénom" className="input-base" />
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Nom" className="input-base" />
            </div>
          </>
        )}
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full bg-white/5 text-zinc-300 text-sm font-bold">Annuler</button>
          <button type="submit" disabled={busy || !offer} className="flex-1 py-3 rounded-full bg-gradient-to-r from-[#00F2FE] to-[#8A2BE2] text-black text-sm font-black disabled:opacity-50" data-testid="affiliate-withdraw-submit">
            {busy ? "…" : "Confirmer"}
          </button>
        </div>
      </form>
    </div>
  );
}
